import { useCreation } from 'ahooks'
import type React from 'react'
import {
  Client,
  Provider,
  cacheExchange,
  errorExchange,
  fetchExchange,
} from 'urql'
import { TRACE_HEADERS } from '../data/app'
import { API_URL } from '../data/environment'
import { generateTraceId } from '../helpers/generateTraceId'
import { getTraceIdentityHeaders } from '../helpers/getTraceIdentityHeaders'

export type ScopedClientTrace = {
  getSessionId: () => string
  getPageId: () => string
}

/**
 * A minimal urql client scoped to a single fixed token, used by the short-link
 * mini-app so a logged-out operator opens their assignment on the token the SMS
 * carried, without touching (or needing) the main session. No offline queue and
 * no refresh: the token is short-lived and the view is a direct read/ack.
 *
 * `token` null builds an unauthenticated client (to resolve the public
 * `shortLink` query before the embedded token is known).
 */
export const createScopedGraphQLClient = (
  token: string | null,
  trace: ScopedClientTrace,
): Client =>
  new Client({
    url: API_URL,
    exchanges: [
      // The only logged-out entry point: a failure here must reach the console
      errorExchange({
        onError: (error) =>
          console.error('[ShortLink GraphQL Error]', error.message),
      }),
      cacheExchange,
      fetchExchange,
    ],
    requestPolicy: 'network-only',
    fetchOptions: () => ({
      headers: {
        // Same trace headers as the main client: the mini-app also mutates
        // (acknowledge) and those calls must stay traceable
        [TRACE_HEADERS.REQUEST_ID]: generateTraceId(),
        ...getTraceIdentityHeaders(),
        [TRACE_HEADERS.SESSION_ID]: trace.getSessionId(),
        [TRACE_HEADERS.PAGE_ID]: trace.getPageId(),
        // Match the main client's header shape (raw token, no "Bearer")
        ...(token ? { authorization: token } : {}),
      },
    }),
  })

/** Scopes a subtree to the link token. `trace` comes from the owner so the
 * resolve and the mini-app requests share one session id. The unauthenticated
 * client (pre-token resolve) is only built through the factory, on purpose. */
export const ScopedGraphQLProvider = ({
  token,
  trace,
  children,
}: React.PropsWithChildren<{
  token: string
  trace: ScopedClientTrace
}>) => {
  // Identity matters (in-flight ack, network-only reads): useCreation, like
  // the main client, not useMemo which React may discard
  const client = useCreation(
    () => createScopedGraphQLClient(token, trace),
    [token, trace],
  )
  return <Provider value={client}>{children}</Provider>
}
